import type { Face, FaceTagBase } from "./brep";
import { faceNew, faceTagHash } from "./brep";
import { planeDistanceTo } from "./plane";
import { planeNormal } from "./plane";
import { planeProjectPolyLine3, polyLine2OnPlane } from "./plane";
import type { Plane } from "./planeTypes";
import { PolygonRoundingMode, polyOp } from "./polyclip/polyClip";
import type { PolyLine2 } from "./polyLine2Types";
import { vec3AngleBetween } from "./vec3";

export function unionCoplanarFaces(faces: Face[], precision: number = 2): Face[] {
  const unioned: Face[] = [];
  const buckets = bucketFacesByAngleDistance(faces, precision);

  for (const bucketKey in buckets) {
    const bucket = buckets[bucketKey];

    if (bucket.length === 0) {
      continue;
    }

    if (bucket.length === 1) {
      unioned.push(...bucket);
      continue;
    }

    const toUnion: [PolyLine2[], Plane][] = [];
    const tags = new Map<string, FaceTagBase>();

    for (const face of bucket) {
      if (face.tags) {
        for (const tag of face.tags) {
          const key = faceTagHash(tag);
          if (!tags.has(key)) {
            tags.set(key, tag);
          }
        }
      }
      toUnion.push([face.loops, face.plane]);
    }

    // arbitrarily use the first face's plane as the destination plane for the union
    const unionPlane = bucket[0].plane;

    const reprojected: PolyLine2[][] = [];
    for (const [loops, loopPlane] of toUnion) {
      reprojected.push(loops.map((loop) => planeProjectPolyLine3(unionPlane, polyLine2OnPlane(loopPlane, loop))));
    }

    const unionedPolygons = polyOp({
      pls: reprojected,
      op: "union",
      roundingMode: PolygonRoundingMode.SNAP_ROUND,
    });

    unioned.push(...unionedPolygons.map((p) => faceNew(p, unionPlane, Array.from(tags.values()))));
  }
  return unioned;
}

export function bucketFacesByAngleDistance(faces: Face[], precision: number = 2): { [key: string]: Face[] } {
  const buckets: { [key: string]: Face[] } = {};

  for (const face of faces) {
    const [nx, ny, nz] = planeNormal(face.plane);
    const angleX = vec3AngleBetween(nx, ny, nz, 1, 0, 0);
    const angleY = vec3AngleBetween(nx, ny, nz, 0, 1, 0);
    const distance = planeDistanceTo(face.plane, 0, 0, 0);
    const bucketKey = `${angleX.toFixed(precision)}:${angleY.toFixed(precision)}:${distance.toFixed(precision)}`;

    if (!buckets[bucketKey]) {
      buckets[bucketKey] = [];
    }

    buckets[bucketKey].push(face);
  }

  return buckets;
}
